import React from 'react';
import { createTheme } from '@mui/material/styles';

// ── Color mode context ─────────────────────────────────────────────────────────
// Consumed by any component that needs to read or toggle the current mode.
export const ColorModeContext = React.createContext({
  mode: 'light',
  toggleColorMode: () => {},
});

// ── Light palette ──────────────────────────────────────────────────────────────
const lightPalette = {
  primary: {
    main: '#6366f1',
    light: '#8693d3ff',
    dark: '#4338ca',
    20: 'rgba(229, 231, 240, 0.17)',
    50: '#f8faff',
    100: '#e0e7ff',
    150: 'rgba(134, 147, 211, 0.28)',
    180: 'rgba(134, 147, 211, 0.62)',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
  },

  sidebar: {
    background: 'linear-gradient(135deg, #f7f8fdff 0%, #ffffffff 100%)',
    hover: 'rgba(99, 102, 241, 0.08)',
    active: 'rgba(99, 101, 241, 0.07)',
    text: '#475569',
    textSecondary: '#64748b',
    border: 'rgba(99, 101, 241, 0.07)',
  },

  secondary: {
    main: '#ad89ad',
    light: '#e0c3fc',
    dark: '#896b89',
    contrastText: '#4e494e',
  },

  activities: {
    research:      { main: '#0ea5e9', light: '#bae6fd', background: 'rgba(14, 165, 233, 0.08)',  border: 'rgba(14, 165, 233, 0.2)'  },
    collaboration: { main: '#10b981', light: '#a7f3d0', background: 'rgba(16, 185, 129, 0.08)',  border: 'rgba(16, 185, 129, 0.2)'  },
    admin:         { main: '#8b5cf6', light: '#ddd6fe', background: 'rgba(139, 92, 246, 0.08)',  border: 'rgba(139, 92, 246, 0.2)'  },
    profile:       { main: '#f59e0b', light: '#fed7aa', background: 'rgba(245, 158, 11, 0.08)',  border: 'rgba(245, 158, 11, 0.2)'  },
    notifications: { main: '#ef4444', light: '#fecaca', background: 'rgba(239, 68, 68, 0.08)',   border: 'rgba(239, 68, 68, 0.2)'   },
    success:       { main: '#059669', light: '#a7f3d0', background: 'rgba(5, 150, 105, 0.08)',   border: 'rgba(5, 150, 105, 0.2)'   },
  },

  layout: {
    background:      '#ffffff8d',
    background2:     'linear-gradient(135deg, #e575759f 0%, #c1ddf884 100%)',
    contentArea:     '#f8f9fa7b',
    darkContentArea: '#f8f9fade',
    surface:         '#ffffff',
    surfaceVariant:  '#f8f9fa',
    border:          'rgba(0, 0, 0, 0.08)',
    shadow:          'rgba(0, 0, 0, 0.02)',
    footer:          '#4486c8',
  },

  social: {
    like: '#ef4444',
    comment: '#6366f1',
    share: '#10b981',
    bookmark: '#f59e0b',
  },

  card: {
    background: '#ffffffdd',
    backgroundSolid: '#ffffff',
  },
};

// ── Dark palette ───────────────────────────────────────────────────────────────
const darkPalette = {
  primary: lightPalette.primary, // accent colour unchanged

  sidebar: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    hover: 'rgba(99, 102, 241, 0.15)',
    active: 'rgba(99, 101, 241, 0.12)',
    text: '#94a3b8',
    textSecondary: '#64748b',
    border: 'rgba(99, 101, 241, 0.12)',
  },

  secondary: {
    main: '#9d7a9d',
    light: '#c9a8e8',
    dark: '#7a5a7a',
    contrastText: '#d4c8d4',
  },

  activities: {
    research:      { main: '#38bdf8', light: '#0c4a6e', background: 'rgba(56, 189, 248, 0.12)',  border: 'rgba(56, 189, 248, 0.25)'  },
    collaboration: { main: '#34d399', light: '#064e3b', background: 'rgba(52, 211, 153, 0.12)',  border: 'rgba(52, 211, 153, 0.25)'  },
    admin:         { main: '#a78bfa', light: '#2e1065', background: 'rgba(167, 139, 250, 0.12)', border: 'rgba(167, 139, 250, 0.25)' },
    profile:       { main: '#fbbf24', light: '#451a03', background: 'rgba(251, 191, 36, 0.12)',  border: 'rgba(251, 191, 36, 0.25)'  },
    notifications: { main: '#f87171', light: '#450a0a', background: 'rgba(248, 113, 113, 0.12)', border: 'rgba(248, 113, 113, 0.25)' },
    success:       { main: '#10b981', light: '#064e3b', background: 'rgba(16, 185, 129, 0.12)',  border: 'rgba(16, 185, 129, 0.25)'  },
  },

  layout: {
    background:      '#0f0f1a',
    background2:     'linear-gradient(135deg, #1a0a2e 0%, #0a1628 100%)',
    contentArea:     'rgba(15, 15, 26, 0.6)',
    darkContentArea: '#1a1a2e',
    surface:         '#1e1e2e',
    surfaceVariant:  '#252535',
    border:          'rgba(255, 255, 255, 0.08)',
    shadow:          'rgba(0, 0, 0, 0.4)',
    footer:          '#3a6fa8',
  },

  social: {
    like: '#f87171',
    comment: '#818cf8',
    share: '#34d399',
    bookmark: '#fbbf24',
  },

  card: {
    background: '#1e1e2edd',
    backgroundSolid: '#1e1e2e',
  },
};

// ── Shared typography & shape (mode-independent) ───────────────────────────────
const typography = {
  fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  h1: { fontWeight: 700, fontSize: '3rem',    lineHeight: 1.2 },
  h2: { fontWeight: 600, fontSize: '2.5rem',  lineHeight: 1.3 },
  h3: { fontWeight: 600, fontSize: '2rem',    lineHeight: 1.4 },
  h4: { fontWeight: 600, fontSize: '1.5rem',  lineHeight: 1.4 },
  h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.5 },
  h6: { fontWeight: 600, fontSize: '1.125rem',lineHeight: 1.5 },
  body1:    { fontSize: '1.4rem',  lineHeight: 1.5  },
  body2:    { fontSize: '1.1rem',  lineHeight: 1.6  },
  feedBody1:{ fontSize: '1.2rem',  lineHeight: 1.1  },
  feedBody2:{ fontSize: '1.1rem',  lineHeight: 1.1  },
  headline: { fontSize: '1.3rem',  lineHeight: 1.3  },
  subtitle1:{ fontSize: '1.2rem',  lineHeight: 1.05 },
  subtitle2:{ fontSize: '1.3rem',  lineHeight: 1.05 },
  caption:  { fontSize: '1rem',   lineHeight: 1.05 },
  tooltip:  { fontSize: '0.9rem', lineHeight: 1.4  },
  sidebarItem:    { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.3 },
  sidebarSection: { fontSize: '1.1rem',  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
};

// ── Theme factory ──────────────────────────────────────────────────────────────
export function createAppTheme(mode = 'light') {
  const cp = mode === 'dark' ? darkPalette : lightPalette;
  const isDark = mode === 'dark';

  return createTheme({
    // Layering: the app bar sits at 1500 (EnhancedAppBar), so every modal (dialogs,
    // drawers) must be above it; menus/popovers are forced to 9999 below so they still
    // open above dialogs, and tooltips/snackbars sit on top of everything.
    zIndex: {
      modal: 2000,
      snackbar: 10040,
      tooltip: 10050,
    },
    palette: {
      mode,
      primary: {
        main:     cp.primary.main,
        light:    cp.primary.light,
        dark:     cp.primary.dark,
        slight: cp.primary[20],
        xxulight: cp.primary[50],
        xulight:  cp.primary[100],
        ulight:   cp.primary[200],
        avatar:   cp.primary[180],
        avatar2:  cp.primary[180],
      },
      secondary: {
        main:         cp.secondary.main,
        light:        cp.secondary.light,
        dark:         cp.secondary.dark,
        contrastText: cp.secondary.contrastText,
      },
      background: {
        default: cp.layout.background,
        paper:   cp.layout.surface,
      },
      text: {
        primary:   isDark ? '#e2e8f0' : '#1f2937',
        secondary: isDark ? '#94a3b8' : '#6b7280',
        contrast: isDark ? '#f8fafc' : cp.secondary.contrastText,
      },
      divider: cp.layout.border,
      // Custom palette extensions
      sidebar:    cp.sidebar,
      activities: cp.activities,
      layout:     cp.layout,
      social:     cp.social,
      card:       cp.card,
    },

    typography,

    shape: { borderRadius: 12 },

    shadows: [
      'none',
      '0 1px 3px rgba(0, 0, 0, 0.05)',
      '0 4px 6px rgba(0, 0, 0, 0.05)',
      '0 10px 15px rgba(0, 0, 0, 0.1)',
      '0 20px 25px rgba(0, 0, 0, 0.1)',
      '0 25px 50px rgba(0, 0, 0, 0.15)',
    ],

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: typography.fontFamily,
            backgroundColor: cp.layout.background,
            colorScheme: mode,
          },
          '.MuiModal-root.MuiMenu-root':    { zIndex: '9999 !important' },
          '.MuiModal-root.MuiPopover-root': { zIndex: '9999 !important' },
        },
      },

      // MUI's default Chip font-size comes from theme.typography.pxToRem(13),
      // which assumes a 16px html root — this app's root is 10px (App.css),
      // so left alone it renders ~62.5% too small. Fixed here in absolute px,
      // scoped to Chip only, so it doesn't also affect Icon/other components
      // that rely on the same default pxToRem-based sizing.
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: '11px',
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            background: `linear-gradient(135deg, ${cp.primary.main} 0%, ${cp.primary.dark} 100%)`,
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${cp.primary.light}20`,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: cp.sidebar.background,
            border: `1px solid ${cp.sidebar.border}`,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: cp.card.background,
            border: `1px solid ${cp.layout.border}`,
            borderRadius: '16px',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 3px 15px ${cp.layout.shadow}`,
              transform: 'translateY(-2px)',
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none', // suppress MUI dark-mode gradient on Paper
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
          },
          contained: {
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-1px)',
            },
          },
        },
      },

      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            '&:hover': { transform: 'scale(1.1)' },
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: { fontSize: '1.3rem' },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            fontSize:   theme.typography.tooltip?.fontSize   ?? '0.82rem',
            lineHeight: theme.typography.tooltip?.lineHeight ?? 1.4,
          }),
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          popper: { zIndex: 9999 },
        },
      },

      MuiDatePicker:     { defaultProps: { slotProps: { popper: { style: { zIndex: 9999 } } } } },
      MuiDateTimePicker: { defaultProps: { slotProps: { popper: { style: { zIndex: 9999 } } } } },
      MuiTimePicker:     { defaultProps: { slotProps: { popper: { style: { zIndex: 9999 } } } } },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: cp.primary.main,
            },
            '&.Mui-focused': {
              backgroundColor: isDark ? 'rgba(30, 30, 46, 0.9)' : 'white',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: cp.primary.main,
              borderWidth: '1px',
            },
          },
          notchedOutline: {
            borderColor: cp.primary[180],
          },
        },
      },

      MuiListItem: {
        styleOverrides: {
          root: {
            '&.activity-research':      { '&:hover': {} },
            '&.activity-collaboration': { '&:hover': {} },
            '&.activity-admin':         { '&:hover': {} },
            '&.activity-profile':       { '&:hover': {} },
          },
        },
      },
    },
  });
}

// ── Backward-compatible default export ────────────────────────────────────────
export const enhancedTheme = createAppTheme('light');

// ── Utility helpers ────────────────────────────────────────────────────────────
export const getActivityColor = (activityType) => {
  const activities = enhancedTheme.palette.activities;
  return activities[activityType] || activities.profile;
};

export const getActivityClassName = (activityType) => `activity-${activityType}`;

// ── Common style objects (light-mode values; override with sx from theme where needed)
export const commonStyles = {
  backButton: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${lightPalette.layout.border}`,
    color: lightPalette.sidebar.text,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      transform: 'translateX(-2px)',
    },
  },

  contentArea: {
    backgroundColor: lightPalette.layout.contentArea,
    marginLeft: '0px',
    position: 'relative',
    pt: 8,
    pb: 2,
  },

  feedProfile: {
    backgroundColor: '#fff',
    border: `1px solid ${lightPalette.primary.light}40`,
    borderRadius: '20px',
    padding: '14px',
    marginBottom: '14px',
  },

  opinionCard: {
    backgroundColor: '#fff',
    border: `1px solid ${lightPalette.layout.border}`,
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '20px',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: `0 8px 25px ${lightPalette.layout.shadow}`,
    },
  },
};

export default enhancedTheme;
